<template>
  <div class="Form-Signal">
    <TextInput 
      :name="'title'" 
      :placeholder="'Заголовок'"
      :errorMessage="props.errors.title" 
      v-model="form.title"
    />
    <div class="Form-Signal__description">
      <div class="Form-Signal__description-box" :class="get_descriptionModifyClass">
        <textarea
          class="Form-Signal__description-field"
          placeholder="Описание"
          autocomplete="off"
          spellcheck="true"
          autocapitalize="off"
          v-model="form.content"
          name="content"
          @input="toInput"
          ref="textarea"
        />
        <EditorSmilesModule @smile="handleSmile" @clickTrigger="handleClickTrigger" />
      </div>
      <p class="Form-Signal__error" v-if="props.errors.content">{{ props.errors.content }}</p>
      <BlockHintAddTicker class="Form-Signal__info" />
    </div>
    <FieldMultipleImages 
      :text="'Загрузить скриншот сигнала'"
      :name="'images'"
      :oldImagesFieldName="'old_images'"
      :error="props.errors.images"
      :subtext="'Скриншот твита, телеграма, сообщения и тд, желательно со временем публикации'"
      :initData="props.initData?.images"
      :startImages="props.initData?.init_images"
      @updateOldImages="toUpdateOldImages"
      @imageUploaded="toUpdateFormData"
    />
    <div class="Form-Signal__coin">
      <div class="Form-Signal__coin-box" :class="get_coinModifyClass">
        <CoinSearch @coinSelected="toSelectCoin" :initData="props.initData?.coin_object" />
        <template v-if="form.coin">
          <div class="Form-Signal__fields-group">
            <TextInput 
              :name="'start_price_usd'" 
              v-model="form.start_price_usd" 
              :label="'Цена'" 
              :errorMessage="props.errors.start_price_usd" 
              :option="'(USD)'" 
            />
          </div>
        </template>
      </div>
      <p class="Form-Signal__error" v-if="props.errors.coin">{{ props.errors.coin }}</p>
    </div>
    <TextInput 
      :name="'link'" 
      :placeholder="'Источник'"
      :errorMessage="props.errors.link" 
      v-model="form.link"
    />
  </div>
</template>

<script setup>
  import { ref, nextTick, watch, computed, onMounted, onBeforeUnmount } from 'vue';
  import { debounce } from 'lodash';
  import { Props } from './scripts/props.js';
  //------------------------
  import TextInput from '@ui/Fields/TextInput/TextInput.vue';
  import FieldMultipleImages from '@shared/UI/Fields/FieldMultipleImages/FieldMultipleImages.vue';
  import EditorSmilesModule from '@entities/Editor/EditorSmilesModule/EditorSmilesModule.vue';
  import BlockHintAddTicker from '@ui/Blocks/BlockHintAddTicker/BlockHintAddTicker.vue';
  import CoinSearch from '@entities/CoinSearch/CoinSearch.vue';
  //------------------------
  const props = defineProps(Props());
  const emit = defineEmits(['formChange']);
  //------------------------
  const TIME_UPDATING_FORM = 300;
  //------------------------
  const form = ref({
    title: '',
    content: '',
    coin: '',
    images: '',
    link: '',
    created_at: '',
    start_price_usd: '',
    coin_object: null,
    images_list_links: [],
  });
  const textarea = ref(null);
  //------------------------
  onMounted(() => {
    initForm();
  });
  //------------------------
  //Класс модификатор для поля description
  const get_descriptionModifyClass = computed(() => {
    return props.errors.content ? 'Form-Signal__description-box--error' : '';
  });
  //Класс модификатор для поля coin
  const get_coinModifyClass = computed(() => {
    return props.errors.coin ? 'Form-Signal__coin-box--error' : '';
  });
  //------------------------
  //Дебаунс на посылание эмита на обновление данных формы
  const debouncedEmit = debounce(() => {
    emit('formChange', form.value);
  }, TIME_UPDATING_FORM);
  //Ввод данных в поле
  const toInput = async() => {
    await nextTick();
    setTextareaHeight();
  }
  //Обработчик для эмита клика, сохраняющий фокус
  const handleClickTrigger = async() => {
    await nextTick();
    textarea.value.focus();
    setTextareaHeight();
  };
  //Рассчитать высоту поля
  const setTextareaHeight = () => {
    if(!textarea.value) return;
    textarea.value.style.maxHeight = '';
    const currentFieldSize = textarea.value.scrollHeight;
    textarea.value.style.maxHeight = `${currentFieldSize}px`;
    textarea.value.style.height = `${currentFieldSize}px`;
  };
  //Вставить смайлик в позицию каретки
  const handleSmile = async(smile) => {
    const cursorPos = textarea.value.selectionStart;
    const currentText = form.value.content;
    form.value.content = currentText.slice(0, cursorPos) + smile + currentText.slice(cursorPos);
    await nextTick();
    const newCursorPos = cursorPos + smile.length;
    textarea.value.selectionStart = textarea.value.selectionEnd = newCursorPos;
    textarea.value.focus();
    setTextareaHeight();
  };
  //Получить коин от компонента SearchCoin
  const toSelectCoin = (coin) => { 
    form.value.start_price_usd = (coin) ? coin.price_usd : '';
    form.value.coin = (coin) ? coin.api_id : '';
    form.value.coin_object = (coin) ? coin : null;
  };
  //Обновить данные по изображению для формы
  const toUpdateFormData = (files) => {
    form.value.images = files;
  };
  //Обновить старые изображения
  const toUpdateOldImages = (images) => {
    form.value.old_images = images;
  };
  const initForm = () => {
    form.value = {
      title: props.initData?.title || '',
      content: props.initData?.content ? props.initData.content.replace(/\\n/g, '\n') : '',
      coin: props.initData?.coin || '',
      link: props.initData?.link || '',
      start_price_usd: props.initData?.start_price_usd || '',
      coin_object: props.initData?.coin_object || null,
      images: props.initData?.images || '',
      old_images: props.initData?.old_images || [],
      images_list_links: props.initData?.images_list_links || []
    };
  
    nextTick(() => {
      if (textarea.value) {
        textarea.value.value = form.value.content;
        setTextareaHeight();
      }
    });
  }
  //------------------------
  watch(form, debouncedEmit, { deep: true });
</script>

<style lang="scss">
  @import "./styles/styles";
</style>